'use client';

import { ROUTES } from '@/constants/routes';
import { signIn, getProviders, getSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { emailSchema } from '@zaikoo/core/domains/auth/schemas/AuthSchemas';
import {
  isWebAuthnSupported,
  startAuthentication,
  type ServerAuthenticationOptions,
} from '@/lib/webauthn';
import { getAuthErrorMessage } from './auth-errors';
import { FormShell, Glyph, OAuthGlyph, useLastUsed } from './parts';

interface KernoSignInProps {
  callbackUrl?: string;
  /** Server-detected device-trust cookie — pre-checks "keep me signed in". */
  hasDeviceTrust?: boolean;
  initialError?: string;
  sessionExpired?: boolean;
  /**
   * Dev design-faithful mode (true on this dev server): render the full designed
   * OAuth set (Google + Apple) even for providers that aren't configured
   * server-side, so the page matches the design. Unconfigured buttons are
   * visual-only (clicking shows a notice, never a broken redirect). In real
   * production auth this is false → only configured providers render.
   */
  showDesignedOAuth?: boolean;
}

export function KernoSignIn({
  callbackUrl = '/dashboard',
  hasDeviceTrust = false,
  initialError,
  sessionExpired = false,
  showDesignedOAuth = false,
}: KernoSignInProps) {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [rememberMe, setRememberMe] = useState(hasDeviceTrust);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(getAuthErrorMessage(initialError));
  const [formReady, setFormReady] = useState(false);
  const [providers, setProviders] = useState<Record<string, { id: string; name: string }> | null>(
    null
  );
  const [lastUsed, markUsed] = useLastUsed();

  const [webAuthnSupported, setWebAuthnSupported] = useState(false);

  const mfaGetChallenge = api.auth.mfaGetChallenge.useMutation();
  const loginPasskeyOptions = api.auth.loginPasskeyOptions.useMutation();
  const verifyPasskeyLogin = api.auth.verifyPasskeyLogin.useMutation();

  useEffect(() => {
    setFormReady(true);
    getProviders().then(setProviders);
    // Detect WebAuthn after mount so SSR + first client render match (no hydration mismatch).
    setWebAuthnSupported(isWebAuthnSupported());
  }, []);

  async function setDeviceTrustCookieIfPending() {
    try {
      const session = await getSession();
      const pendingToken = (session as { pendingDeviceToken?: string } | null)?.pendingDeviceToken;
      if (pendingToken) {
        await fetch('/api/auth/set-device-cookie', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: pendingToken }),
        });
      }
    } catch {
      /* device-trust cookie failure must not block navigation */
    }
  }

  const handleOAuth = async (provider: 'google' | 'apple') => {
    setError('');
    // Providers list still loading — ignore the click rather than show a false
    // "not configured" notice for a provider that may in fact be configured.
    if (!providers) return;
    // Design-faithful mode may render a button for a provider that isn't
    // configured server-side — never fire a redirect that would 404 / error.
    if (!providers[provider]) {
      setError(
        `${provider === 'apple' ? 'Apple' : 'Google'} sign-in isn't configured in this environment yet.`
      );
      return;
    }
    markUsed(provider);
    setBusy(true);
    try {
      await signIn(provider, { callbackUrl });
    } catch {
      setError('An error occurred. Please try again.');
      setBusy(false);
    }
  };

  const handleCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !pw) {
      setError('Please enter both email and password.');
      return;
    }
    setBusy(true);
    setError('');
    markUsed('email');
    try {
      const result = await signIn('credentials', {
        email,
        password: pw,
        rememberMe: rememberMe ? 'true' : 'false',
        redirect: false,
      });
      if (result?.error) {
        try {
          const mfa = await mfaGetChallenge.mutateAsync({ email });
          if (mfa.mfaRequired) {
            await fetch('/api/auth/mfa-session', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ challengeToken: mfa.challengeToken, email }),
            });
            router.push(ROUTES.AUTH.MFA_VERIFY);
            return;
          }
        } catch {
          /* not an MFA issue — fall through to generic error */
        }
        setError('Invalid email or password.');
        setBusy(false);
      } else if (result?.ok) {
        if (rememberMe) await setDeviceTrustCookieIfPending();
        router.push(callbackUrl);
      } else {
        setBusy(false);
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
      setBusy(false);
    }
  };

  const handlePasskey = async () => {
    setError('');
    markUsed('passkey');
    setBusy(true);
    try {
      const options = await loginPasskeyOptions.mutateAsync();
      const assertion = await startAuthentication(options as ServerAuthenticationOptions);
      const verification = await verifyPasskeyLogin.mutateAsync(assertion);
      const signInResult = await signIn('credentials', {
        email: `passkey:${verification.userId}`,
        password: `passkey-token:${verification.passkeyId}`,
        redirect: false,
      });
      if (signInResult?.ok) {
        router.push(callbackUrl);
      } else {
        setError('Failed to create session after passkey verification.');
        setBusy(false);
      }
    } catch (err) {
      if (!(err instanceof Error && err.name === 'NotAllowedError')) {
        setError(err instanceof Error ? err.message : 'Passkey sign-in failed.');
      }
      setBusy(false);
    }
  };

  const handleMagic = async () => {
    if (!emailSchema.safeParse(email).success) {
      setError('Enter a valid email above to receive a magic link.');
      return;
    }
    setError('');
    markUsed('magic');
    setBusy(true);
    try {
      const result = await signIn('email', { email, redirect: false, callbackUrl });
      if (result?.error) {
        setError('Failed to send magic link. Please try again.');
        setBusy(false);
      } else {
        router.push(ROUTES.AUTH.VERIFY_REQUEST);
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
      setBusy(false);
    }
  };

  // Render a provider's button when it's configured, or — in dev design-faithful
  // mode — always, so the designed Google + Apple row matches the design.
  const showGoogle = Boolean(providers?.google) || showDesignedOAuth;
  const showApple = Boolean(providers?.apple) || showDesignedOAuth;
  const hasOAuth = showGoogle || showApple;

  // In dev, preserve the ?preview=1 hatch across the cross-link so navigating to
  // sign-up doesn't bounce off the "already authenticated" redirect.
  const signupHref = showDesignedOAuth ? `${ROUTES.AUTH.SIGNUP}?preview=1` : ROUTES.AUTH.SIGNUP;

  return (
    <FormShell
      headRight={
        <>
          New here?
          <a
            href={signupHref}
            onClick={e => {
              e.preventDefault();
              router.push(signupHref);
            }}
          >
            Create an account
          </a>
        </>
      }
    >
      <div className="auth-eyebrow">
        <span className="dot" />
        Sign in
      </div>
      <h1 className="auth-title">Welcome back.</h1>
      <p className="auth-sub">Sign in to your Kerno workspace to keep operations moving.</p>

      {sessionExpired && !error && (
        <div className="auth-banner warn">
          <div>Your session expired. Please sign in again.</div>
        </div>
      )}
      {error && (
        <div className="auth-banner error">
          <span aria-hidden="true">
            <Glyph name="alert" size={16} />
          </span>
          <div>{error}</div>
        </div>
      )}

      {hasOAuth && (
        <>
          <div className="auth-oauth auth-oauth--two">
            {showGoogle && (
              <button
                type="button"
                className={`oauth-btn ${lastUsed === 'google' ? 'is-last-used' : ''}`}
                onClick={() => handleOAuth('google')}
                disabled={busy}
              >
                <span className="oauth-glyph">
                  <OAuthGlyph provider="google" />
                </span>
                <span className="oauth-label">Continue with Google</span>
                {lastUsed === 'google' && <span className="last-used-pill">Last used</span>}
              </button>
            )}
            {showApple && (
              <button
                type="button"
                className={`oauth-btn ${lastUsed === 'apple' ? 'is-last-used' : ''}`}
                onClick={() => handleOAuth('apple')}
                disabled={busy}
              >
                <span className="oauth-glyph">
                  <OAuthGlyph provider="apple" />
                </span>
                <span className="oauth-label">Continue with Apple</span>
                {lastUsed === 'apple' && <span className="last-used-pill">Last used</span>}
              </button>
            )}
          </div>
          <div className="auth-divider">or with email</div>
        </>
      )}

      <form
        data-testid="auth-signin-form"
        data-ready={formReady ? 'true' : 'false'}
        onSubmit={handleCredentials}
      >
        <div className="auth-field">
          <div className="auth-field-label">Work email</div>
          <input
            className="auth-input"
            type="email"
            placeholder="you@company.com"
            autoComplete="email webauthn"
            aria-label="Work email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="auth-field">
          <div className="auth-field-label">Password</div>
          <div className="auth-input-group">
            <input
              className="auth-input"
              type={showPw ? 'text' : 'password'}
              placeholder="••••••••••"
              autoComplete="current-password webauthn"
              aria-label="Password"
              value={pw}
              onChange={e => setPw(e.target.value)}
              disabled={busy}
            />
            <button
              type="button"
              className="auth-input-affix"
              onClick={() => setShowPw(s => !s)}
              aria-label={showPw ? 'Hide password' : 'Show password'}
            >
              <Glyph name={showPw ? 'eye-off' : 'eye'} size={16} />
            </button>
          </div>
        </div>

        <div className="auth-checkbox-row">
          <input
            id="kerno-remember"
            type="checkbox"
            checked={rememberMe}
            onChange={e => setRememberMe(e.target.checked)}
            disabled={busy}
          />
          <label htmlFor="kerno-remember">Keep me signed in on this device</label>
        </div>

        <div className="auth-submit-row">
          <button type="submit" className="auth-btn primary" disabled={busy}>
            {busy ? (
              <>
                <span className="spin" />
                Signing you in…
              </>
            ) : (
              <>
                Sign in <Glyph name="arrow-right" size={15} />
              </>
            )}
          </button>
        </div>
      </form>

      {(webAuthnSupported || providers?.email) && (
        <div className="auth-alt-row">
          {webAuthnSupported && (
            <button type="button" className="auth-alt-link" onClick={handlePasskey} disabled={busy}>
              <Glyph name="fingerprint" size={15} /> Sign in with a passkey
              {lastUsed === 'passkey' && <span className="last-used-pill">Last used</span>}
            </button>
          )}
          {providers?.email && (
            <button
              type="button"
              className="auth-alt-link auth-alt-link-muted"
              onClick={handleMagic}
              disabled={busy || !email}
            >
              <Glyph name="mail" size={14} /> Email me a link
              {lastUsed === 'magic' && <span className="last-used-pill">Last used</span>}
            </button>
          )}
        </div>
      )}

      <p className="auth-consent">
        By continuing you agree to our <a href="/terms">Terms</a> and{' '}
        <a href="/privacy">Privacy Policy</a>. Sign-in attempts are logged for security.
      </p>
    </FormShell>
  );
}
